import { ReactElement, useEffect } from 'react'
import NextLink from 'next/link'
import { BiShieldQuarter } from 'react-icons/bi'
import { IoPricetag } from 'react-icons/io5'
import { useTranslations } from 'next-intl'
import {
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Stack,
  StackProps,
  TableContainer,
  Text,
  Box,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { createColumnHelper } from '@tanstack/react-table'

import { FACILITY_PLACEHOLDER } from '@/constants'
import { IFacilityNode } from '@/types/facilities.interface'
import { IMinerNode } from '@/types/miner.interface'
import FacilityDetailCard from './FacilityDetailCard'
import { ArrowLeftIcon } from '@/components/icons/ArrowLeftIcon'
import { CheckIcon } from '@/components/icons/CheckIcon'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { ProductsListTable } from '@/components/productsListTable'
import { RightArrowIcon } from '@/components/icons/RightArrowIcon'
import { SurveillanceIcon } from '@/components/icons/SurveillanceIcon'
import { PaymentPlanModal } from '@/features/hardware/PaymentPlanModal'
import { formatPrice } from '@/utils/formatPrice'
import { getImages } from '@/utils/getImages'

interface FeatureProps {
  text: string | ReactElement
  iconBg: string
  icon?: ReactElement
}

const Feature = ({ text, icon, iconBg }: FeatureProps) => {
  return (
    <Stack direction={'row'} align={'center'}>
      <Flex
        w={8}
        h={8}
        align={'center'}
        justify={'center'}
        rounded={'full'}
        bg={iconBg}
        flexWrap={'nowrap'}
      >
        {icon}
      </Flex>
      <Text fontWeight={600} fontSize={14}>
        {text}
      </Text>
    </Stack>
  )
}

interface Types extends StackProps {
  facility: IFacilityNode
  products: ReactElement
}

export function FacilityInformation({ facility, products }: Types) {
  const hardwareTableRef = useRef()
  const formRef = useRef<HTMLParagraphElement>(null)
  const hasTableOverflow =
    hardwareTableRef?.current?.scrollWidth >
      hardwareTableRef?.current?.clientWidth || false
  const [currentImage, setCurrentImage] = useState('')
  const [listImages, setListImages] = useState<Array<string>>([])
  const t = useTranslations()
  const {
    isOpen: isPaymentPlanOpen,
    onOpen: onPaymentPlanOpen,
    onClose: onPaymentPlanClose,
  } = useDisclosure()

  const {
    facilityAvailability,
    facilitiesDescription,
    facilitiesName,
    facilitiesSecurityOnSite,
    facilitiesSecurityCameras,
    facilitiesSetupFees,
    facilitiesImagesCsvPrioritized,
    countryName,
  } = facility

  const { mainImage } = getImages(
    facilitiesImagesCsvPrioritized,
    FACILITY_PLACEHOLDER
  )

  // const price = formatPrice(Number(facilitiesPricePerKwh))
  const showEmptyState =
    facilityAvailability === 'fully_booked' ||
    facilityAvailability === 'coming_soon'

  const facilitiesStatusColor = () => {
    // TODO: Change this from being always true to actually checking the hardware from a query
    if (facilityAvailability === 'available') {
      return 'green.450'
    }
    if (facilityAvailability === 'coming_soon') {
      return 'orange.450'
    }
    if (facilityAvailability === 'fully_booked') {
      return 'red.550'
    }
    return 'brand.500'
  }

  const facilitiesCountryFlag = () => {
    if (countryName) {
      if (countryName === 'United States') {
        return '/icons/flags/us.svg'
      }
      if (countryName === 'Canada') {
        return '/icons/flags/canada.svg'
      }
      return '/icons/flags/globe.svg'
    }
  }
  useEffect(() => {
    /* get random images of facility maximum 3 */
    const arrayImages = [...facilitiesImagesCsvPrioritized]
    const arrayLength = arrayImages.length > 3 ? 3 : arrayImages.length
    arrayImages.shift()
    const randomImages = arrayImages
      .sort(() => Math.random() - Math.random())
      .slice(0, arrayLength)
    setListImages(randomImages)
  }, [])

  /* create column helper for hardware details table, get all attributes types */
  const allMinersColumnsHelper = createColumnHelper<IMinerNode>()

  /* build column headers for hardware details table */
  const allMinersColumns = [
    allMinersColumnsHelper.accessor('minerModelName', {
      cell: (info) => info.getValue(),
      header: 'Miner',
    }),
    allMinersColumnsHelper.accessor('hostedTargetOnlineDate', {
      cell: (info) => info.getValue(),
      header: '',
    }),
    allMinersColumnsHelper.accessor('productCondition', {
      cell: (info) => info.getValue(),
      header: t('common.labels.condition'),
    }),
    allMinersColumnsHelper.accessor('productAvailableQuantity', {
      cell: (info) => info.getValue(),
      header: t('hardware-list.labels.available-quantity'),
    }),
    allMinersColumnsHelper.accessor('monthlyHostingCharges', {
      cell: (info) => info.getValue(),
      header: t('hardware-list.labels.hosting-fee'),
    }),
    allMinersColumnsHelper.accessor('minerModelHashratePerTerahash', {
      cell: (info) => info.getValue(),
      header: t('common.labels.hashrate'),
    }),
    allMinersColumnsHelper.accessor('minerModelPowerConsumptionWatts', {
      cell: (info) => info.getValue(),
      header: t('common.labels.power'),
    }),
    allMinersColumnsHelper.accessor('productPricePerHashrate', {
      cell: (info) => info.getValue(),
      header: `$/${t('common.labels.hashrate')}`,
    }),
    allMinersColumnsHelper.accessor('productListingPrice', {
      cell: (info) => info.getValue(),
      header: t('common.labels.price'),
    }),
  ]
  const whitebg = useColorModeValue('white', 'transparent')
  const textblack = useColorModeValue('black', 'white')

  return (
    <>
      <Box
        mx={'auto'}
        mt={2}
        // maxW={{ sm: '540px', md: '720px', lg: '900px', xl: '1000px' }}
        w={'full'}
      >
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          justifyContent={'center'}
        >
          <Stack
            spacing={1}
            rounded={'md'}
            bg={'transparent'}
            mx={{ base: '20px', lg: 0 }}
            px={{ base: '10px' }}
            pb={{ base: '20px' }}
            mb={{ base: '20px' }}
            minW={{ base: '300px', lg: '450px' }}
            maxW={{ base: 'full', lg: '800px' }}
          >
            <Box>
              <NextLink href={'/facilities' ?? '#'} passHref>
                <Button
                  w={'8.1rem'}
                  as={'a'}
                  bg={'transparent'}
                  _active={{ bg: 'transparent' }}
                  _hover={{ bg: 'transparent' }}
                >
                  <ArrowLeftIcon mr={2} mb={1} />
                  {t('facilities-list.more-facilities')}
                </Button>
              </NextLink>
            </Box>
            <Flex direction={'column'} alignContent={'center'}>
              <Image
                rounded={'md'}
                alt={'feature image'}
                src={currentImage ? currentImage : mainImage}
                objectFit={'cover'}
                h="450px"
                fallbackSrc={FACILITY_PLACEHOLDER}
              />
              <Box pt={{ base: '10px' }}>
                <Flex justify={'center'}>
                  {listImages?.map((url, index) => (
                    <Image
                      key={`facility-image-${index}`}
                      w={1 / 3}
                      rounded={'lg'}
                      p={1}
                      src={url}
                      alt={facilitiesName}
                      onClick={() => {
                        setCurrentImage(url)
                        return null
                      }}
                      fallbackSrc={FACILITY_PLACEHOLDER}
                    />
                  ))}
                </Flex>
              </Box>
            </Flex>
          </Stack>

          {/* begin right half / facility info section */}

          <Stack
            spacing={4}
            rounded={'md'}
            bg={whitebg}
            pt={{ base: '10px' }}
            mx={{ base: '20px' }}
            my={{ base: 'auto' }}
            px={{ base: '20px', lg: '40px' }}
            pb={{ base: '20px' }}
            maxW={{ base: 'full', lg: '500px' }}
            minH={{ lg: '500px' }}
          >
            <Flex direction={'column'} alignItems={'left'} py={3}>
              <Flex direction={'row'} alignItems={'center'}>
                <Image
                  w={'26px'}
                  h={'20px'}
                  src={facilitiesCountryFlag()}
                  alt={'Facility country goes here'}
                  mr={3}
                  rounded={'sm'}
                  bg={whitebg}
                />
                <Text fontWeight={'semibold'} fontSize={'lg'} as="h1">
                  {facilitiesName}
                </Text>
                <Text
                  ml={3}
                  // textTransform={'uppercase'}
                  color={facilitiesStatusColor()}
                  fontWeight={600}
                  fontSize={'sm'}
                  // bg={useColorModeValue('blue.50', 'blue.900')}
                  alignSelf={'flex-center'}
                  rounded={'md'}
                >
                  {facilityAvailability === 'fully_booked' &&
                    t('facilities-list.fully-booked')}
                  {facilityAvailability === 'coming_soon' &&
                    t('facilities-list.coming-soon')}
                  {facilityAvailability === 'available' &&
                    t('facilities-list.units-available')}
                </Text>
              </Flex>
              <Text mt={3} color={textblack} fontSize={'sm'}>
                {facilitiesDescription}
              </Text>
            </Flex>

            <Flex>
              <Stack mr={1}>
                <Feature
                  icon={<SurveillanceIcon w={5} h={5} />}
                  iconBg={useColorModeValue('transparent', 'transparent')}
                  text={`Video Surveillance: ${
                    facilitiesSecurityCameras ? 'Yes' : 'No'
                  }`}
                />
                <Feature
                  icon={
                    <Icon as={BiShieldQuarter} color={textblack} w={5} h={5} />
                  }
                  iconBg={useColorModeValue('transparent', 'transparent')}
                  text={`On-site security: ${
                    facilitiesSecurityOnSite ? 'Yes' : 'No'
                  }`}
                />
              </Stack>
              <Stack>
                <Feature
                  icon={<Icon as={IoPricetag} color={textblack} w={5} h={5} />}
                  iconBg={'transparent'}
                  text={`Setup Fees: ${formatPrice(
                    Number(facilitiesSetupFees)
                  )}/unit`}
                />
              </Stack>
            </Flex>
          </Stack>
        </Flex>
      </Box>
      <ContainerBase mt="20">
        {!showEmptyState ? (
          <>
            <Text
              fontSize={{ base: '2xl', lg: '4xl' }}
              fontWeight="bold"
              mb="5"
              textAlign={'center'}
              ref={formRef}
            >
              {t('facilities-list.available-units-new-reservations')}
            </Text>
            <TableContainer
              display={{ base: 'none', lg: 'block' }}
              whiteSpace={'break-spaces'}
              overflowX={'auto'}
              overflowY={'auto'}
              position="relative"
              ref={hardwareTableRef}
            >
              <ProductsListTable
                columns={allMinersColumns}
                data={products?.allAvailableProducts?.nodes || []}
                hasTableOverflow={hasTableOverflow}
                enableSortingRemoval
                paymentPlanButton={
                  <Box my={2}>
                    <Link
                      fontSize="xs"
                      fontWeight={400}
                      onClick={onPaymentPlanOpen}
                      textDecoration="underline"
                    >
                      {t('payment-plan.labels.payment-plan')}{' '}
                      {t('payment-plan.labels.eligible')}
                    </Link>
                  </Box>
                }
              />
            </TableContainer>
            <Stack display={{ base: 'flex', lg: 'none' }} w="full">
              <FacilityDetailCard
                items={products?.allAvailableProducts?.nodes || []}
              />
            </Stack>
          </>
        ) : (
          <>
            <Text
              display={{ base: 'none', lg: 'block' }}
              fontSize={'2xl'}
              fontWeight={600}
              lineHeight="46px"
              textAlign="center"
            >
              {facilityAvailability === 'coming_soon' &&
                t('facilities-list.has-machines-coming-soon')}
              {facilityAvailability === 'fully_booked' &&
                t('facilities-list.facility-fully-booked')}
            </Text>
            <Text
              display={{ base: 'none', lg: 'block' }}
              fontSize="22px"
              lineHeight="30px"
              mb="8"
              textAlign="center"
            >
              {t('facilities-list.view-facilities-by-availability')}
            </Text>
            <Flex
              h={{ base: 'auto', xl: '232px' }}
              direction={{ base: 'column', xl: 'row' }}
              justify="space-evenly"
            >
              <Stack
                rounded="15"
                background="black"
                mb={{ base: '5', xl: '0' }}
              >
                <Flex
                  w={{ base: 'auto', xl: '538px' }}
                  h={{ base: '424px', md: '232px', lg: '232px' }}
                  direction={{ base: 'column', md: 'row' }}
                  justifyContent={{ base: 'space-between', md: 'normal' }}
                  position="relative"
                >
                  <Box
                    w={{ base: 'auto', md: '65%', lg: '52%' }}
                    pt="22px"
                    pl={{ base: '25px', md: '35px' }}
                    pr={{ base: '25px', lg: '0' }}
                  >
                    <Text
                      fontWeight={400}
                      fontSize="32px"
                      lineHeight="39px"
                      color="white"
                    >
                      {t('facilities-list.world-best-mining')}
                    </Text>
                    <NextLink href="/facilities">
                      <Text
                        fontFamily="Inter"
                        fontWeight={600}
                        fontSize="lg"
                        color="brandGreen.50"
                        cursor="pointer"
                        pt={{ base: '15px', md: '16', lg: '26px' }}
                      >
                        {t('facilities-list.view-available-facilities')}
                        <RightArrowIcon ml={2} />
                      </Text>
                    </NextLink>
                  </Box>
                  <Flex
                    w={{ base: 'auto', md: '50%' }}
                    alignItems={{ base: 'flex-end', md: 'center' }}
                    justifyContent={{ base: 'center', md: 'flex-end' }}
                  >
                    <Flex
                      bg="#FDFBFB"
                      position="absolute"
                      rounded="2xl"
                      alignItems="center"
                      justifyContent="space-evenly"
                      w="117px"
                      h="27px"
                      top={{ base: '55%', sm: '8' }}
                      right={{
                        base: '4%',
                        sm: '21%',
                        lg: '15.5%',
                        xl: '27%',
                      }}
                    >
                      <CheckIcon bg="green.450" rounded="10" color="white" />
                      <Text
                        color="green.450"
                        fontWeight={600}
                        fontSize="xs"
                        lineHeight="18px"
                        textTransform="capitalize"
                        letterSpacing="wide"
                      >
                        {t('facilities-list.units-available')}
                      </Text>
                    </Flex>
                    <Image
                      h="211px"
                      w={{ base: '86%', md: '232px' }}
                      src="/img/host-facilities-short.png"
                      rounded={{ base: '0', md: '5' }}
                      alt=""
                      boxShadow={{
                        base: '-1px -9px 49px -9px #27AE60',
                        md: '-12px 0px 39px -3px #27AE60',
                      }}
                    />
                  </Flex>
                </Flex>
              </Stack>
              <Stack background="black" rounded="15">
                <Box
                  w={{ base: 'auto', xl: '538px' }}
                  h={{ base: '424px', md: '232px', lg: '232px' }}
                  bgImage={{
                    base: `url('/img/units-mobile.png')`,
                    md: `url('/img/units.png')`,
                  }}
                  bgRepeat="no-repeat"
                  bgPosition={{ base: 'bottom', md: 'right', lg: 'right' }}
                  bgSize="contain"
                  rounded="15"
                >
                  <Box
                    w={{ base: 'auto', md: '65%' }}
                    pt="22px"
                    pl={{ base: '26px', md: '35px' }}
                    pr={{ base: '9px', lg: '0' }}
                  >
                    <Text
                      fontWeight={400}
                      fontSize="32px"
                      lineHeight="41px"
                      color="white"
                    >
                      {t('facilities-list.everyone-be-able-mine')}
                    </Text>
                    <NextLink href="/hardware">
                      <Text
                        fontFamily="Inter"
                        fontWeight={600}
                        color="#EE6F43"
                        cursor="pointer"
                        pt={{ base: '15px', md: '16', lg: '26px' }}
                        fontSize="lg"
                      >
                        {t('facilities-list.shop-mining-hw')}
                        <RightArrowIcon ml={2} />
                      </Text>
                    </NextLink>
                  </Box>
                </Box>
              </Stack>
            </Flex>
          </>
        )}
      </ContainerBase>
      <PaymentPlanModal
        isOpen={isPaymentPlanOpen}
        onClose={onPaymentPlanClose}
      />
    </>
  )
}
